import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@paulownia/shared/components/ui/button';
import { ChevronLeft, ChevronRight, Rocket, Loader2 } from 'lucide-react';
import { createTenantSelfService } from '@paulownia/shared/services/tenant';
import { initFirebase } from '@paulownia/shared/lib/firebase';
import { OnboardingProgress } from './components/OnboardingProgress';
import { MarketRequestModal } from './components/MarketRequestModal';
import { TenantInfoStep } from './steps/TenantInfoStep';
import { MarketSelectionStep } from './steps/MarketSelectionStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { useOnboardingState } from './hooks/useOnboardingState';
import { useSlugValidation } from './hooks/useSlugValidation';

/**
 * Main onboarding wizard component.
 *
 * Manages the 3-step flow for new merchant onboarding:
 * 1. Business information (name, slug, optional details)
 * 2. Market selection (select up to 3 markets)
 * 3. Confirmation and creation
 */
export function OnboardingWizard() {
  const navigate = useNavigate();
  const [isMarketRequestModalOpen, setIsMarketRequestModalOpen] = useState(false);

  const {
    currentStep,
    formData,
    isSubmitting,
    isCurrentStepValid,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updateTenantInfo,
    setSlugStatus,
    setIsSubmitting,
    addMarket,
    removeMarket,
    isMarketSelected,
    canSelectMoreMarkets,
    maxMarkets,
  } = useOnboardingState();

  // Slug validation with debounced API check
  const { status: slugStatus, validateSlug } = useSlugValidation({
    minLength: 3,
    debounceMs: 500,
    onStatusChange: setSlugStatus,
  });

  // Handle slug change with validation
  const handleSlugChange = useCallback(
    (slug: string) => {
      updateTenantInfo({ slug });
      validateSlug(slug);
    },
    [updateTenantInfo, validateSlug]
  );

  // Handle market request modal
  const handleRequestMarket = useCallback(() => {
    setIsMarketRequestModalOpen(true);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!isCurrentStepValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Transform form data for the Cloud Function
      const { tenantInfo, marketSelection } = formData;

      const result = await createTenantSelfService({
        name: tenantInfo.name.trim(),
        slug: tenantInfo.slug.trim().toLowerCase(),
        description: tenantInfo.description?.trim() || undefined,
        phoneNumber: tenantInfo.phoneNumber?.trim() || undefined,
        openingHours: tenantInfo.openingHours?.trim() || undefined,
        markets: marketSelection.selectedMarkets.map((m) => ({
          marketId: m.marketId,
        })),
      });

      if (result.success) {
        // Force refresh the user's token to get updated claims
        const { auth } = initFirebase();
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
        }

        toast.success('Commerce créé avec succès !', {
          description: 'Vous allez être redirigé vers votre tableau de bord.',
        });

        // Navigate to dashboard after a short delay for the toast to show
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error: unknown) {
      console.error('Error creating tenant:', error);

      // Extract error message from Firebase function error
      let errorMessage = 'Une erreur est survenue lors de la création du commerce.';

      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };

        // Handle specific error codes
        if (firebaseError.code === 'functions/already-exists') {
          errorMessage = 'Ce slug est déjà utilisé. Veuillez en choisir un autre.';
        } else if (firebaseError.code === 'functions/failed-precondition') {
          errorMessage = 'Vous avez déjà un commerce. Utilisez les paramètres pour le modifier.';
        } else if (firebaseError.code === 'functions/invalid-argument') {
          // Extract the message from the error
          errorMessage = firebaseError.message || 'Données invalides. Veuillez vérifier vos informations.';
        } else if (firebaseError.code === 'functions/unauthenticated') {
          errorMessage = 'Votre session a expiré. Veuillez vous reconnecter.';
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        }
      }

      toast.error('Erreur', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isCurrentStepValid, isSubmitting, navigate, setIsSubmitting]);

  // Handle continue button
  const handleContinue = useCallback(() => {
    if (isLastStep) {
      handleSubmit();
    } else {
      goToNextStep();
    }
  }, [isLastStep, handleSubmit, goToNextStep]);

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <TenantInfoStep
            data={formData.tenantInfo}
            slugStatus={slugStatus}
            onUpdate={updateTenantInfo}
            onSlugChange={handleSlugChange}
          />
        );
      case 1:
        return (
          <MarketSelectionStep
            selectedMarkets={formData.marketSelection.selectedMarkets}
            maxMarkets={maxMarkets}
            canSelectMore={canSelectMoreMarkets}
            onAddMarket={addMarket}
            onRemoveMarket={removeMarket}
            isMarketSelected={isMarketSelected}
            onRequestMarket={handleRequestMarket}
          />
        );
      case 2:
        return <ConfirmationStep data={formData} isSubmitting={isSubmitting} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Market request modal */}
      <MarketRequestModal
        isOpen={isMarketRequestModalOpen}
        onClose={() => setIsMarketRequestModalOpen(false)}
      />

      {/* Progress indicator - fixed at top */}
      <div className="flex-shrink-0 px-4 py-6 border-b bg-white">
        <OnboardingProgress currentStep={currentStep} onStepClick={goToStep} />
      </div>

      {/* Step content - scrollable */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-lg mx-auto">{renderStepContent()}</div>
      </div>

      {/* Navigation footer - fixed at bottom */}
      <div className="flex-shrink-0 border-t bg-white p-4">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
          {/* Back button */}
          <Button
            type="button"
            variant="outline"
            onClick={goToPreviousStep}
            disabled={currentStep === 0 || isSubmitting}
            className="min-w-[100px]"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>

          {/* Continue/Submit button */}
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!isCurrentStepValid || isSubmitting}
            className="min-w-[160px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Création...
              </>
            ) : isLastStep ? (
              <>
                <Rocket className="h-4 w-4 mr-2" />
                Créer mon commerce
              </>
            ) : (
              <>
                Continuer
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
